import { useEffect, useState } from 'react';

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

type PwaListener = () => void;

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<PwaListener>();

function notify(): void {
  for (const listener of listeners) listener();
}

function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iPadDesktopMode = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/i.test(ua) || iPadDesktopMode;
}

function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installed = true;
    notify();
  });
}

export function subscribePwa(listener: PwaListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getPwaState(): { available: boolean; ios: boolean; installed: boolean } {
  const standalone = installed || isStandaloneMode();
  const ios = isIosDevice();
  return {
    available: !standalone && (deferredPrompt !== null || ios),
    ios,
    installed: standalone,
  };
}

export async function promptPwaInstall(): Promise<boolean> {
  const prompt = deferredPrompt;
  if (!prompt) return false;
  await prompt.prompt();
  const choice = await prompt.userChoice;
  deferredPrompt = null;
  if (choice.outcome === 'accepted') installed = true;
  notify();
  return choice.outcome === 'accepted';
}

export function usePwaInstall() {
  const [state, setState] = useState(getPwaState);

  useEffect(() => subscribePwa(() => setState(getPwaState())), []);

  return {
    ...state,
    isIos: state.ios,
    install: promptPwaInstall,
  };
}
